import { compact } from 'lodash';

import { WorkflowStepEnumType } from '@shared/enums/workflow-step.enum';
import { EnumerationItem } from '@shared/interfaces/enumeration-item';
import { RestPortfolioModel, RestPropertyModel } from '@shared/interfaces/rest-portfolio-model.interface';
import { RestUserModel } from '@shared/interfaces/user.interface';
import { RestTemplateModel } from '@template/models/rest-template-model.interface';

import {
  DueDateBaseDate,
  DynamicInspectionAssigneeSelectionMode,
  DynamicPropertyType,
  DynamicSchedule,
  RestDynamicInspectionModel,
} from './rest-inspections-model.interface';

export class DynamicInspectionModel {
  public readonly entityDetails: RestDynamicInspectionModel;
  public readonly inspectionTemplate?: RestTemplateModel;
  private readonly workflowSteps: EnumerationItem[];

  readonly users: RestUserModel[];
  readonly portfolios?: RestPortfolioModel[];
  readonly assigneeUserName?: string;
  readonly assigneeUserTooltip?: string;
  readonly currentProperty?: number;

  readonly id: number;
  readonly inspectionType: string;
  readonly propertyId: number;
  readonly propertyName: string;
  readonly portfolioName: string;
  readonly portfolioId: number;
  readonly due: string;
  readonly notify: string;
  readonly step: string;
  readonly workflowStep: WorkflowStepEnumType;
  readonly notifyWhenDone: RestUserModel[];
  readonly assigneeUser: RestUserModel;
  readonly unassigned: boolean;
  constructor(
    entityDetails: RestDynamicInspectionModel,
    inspectionTemplate: RestTemplateModel | undefined,
    properties: RestPropertyModel[],
    workflowSteps: EnumerationItem[],
    users: RestUserModel[],
    portfolios?: RestPortfolioModel[],
    assigneeUserName?: string,
    assigneeUserTooltip?: string,
    currentProperty?: number
  ) {
    this.entityDetails = entityDetails;
    this.inspectionTemplate = inspectionTemplate;
    this.workflowSteps = workflowSteps;
    this.users = users;
    this.portfolios = portfolios;
    this.assigneeUserName = assigneeUserName;
    this.assigneeUserTooltip = assigneeUserTooltip;
    this.currentProperty = currentProperty;

    this.id = entityDetails.id;
    this.inspectionType = inspectionTemplate?.name;
    this.workflowStep =
      entityDetails.dynamicallyScheduledOn === DynamicSchedule.MoveIn
        ? WorkflowStepEnumType.MoveIn
        : entityDetails.dynamicallyScheduledOn === DynamicSchedule.MoveOut
        ? WorkflowStepEnumType.MoveOut
        : entityDetails.dynamicallyScheduledOn === DynamicSchedule.MakeReady
        ? WorkflowStepEnumType.MakeReady
        : WorkflowStepEnumType.Notice;
    const stepConfig = this.workflowSteps.find(s => s.enumerationValue === this.workflowStep);
    this.step = stepConfig?.displayText;

    if (this.entityDetails.propertyType === DynamicPropertyType.Property) {
      this.propertyId = entityDetails.propertyId;
      this.portfolioId = entityDetails.portfolioId;
      this.propertyName = properties.find(p => p.id === entityDetails.propertyId)?.name;
    }
    if (this.entityDetails.propertyType === DynamicPropertyType.Portfolio && !this.entityDetails.propertyIds?.length) {
      //TODO: check if it has properties
      this.propertyId = -1;
      this.portfolioId = entityDetails.propertyOrPortfolioId;
    }
    if (this.entityDetails.propertyType === DynamicPropertyType.Portfolio && this.entityDetails.propertyIds?.length) {
      const propertyId = this.currentProperty || entityDetails.propertyIds[0];
      this.propertyName = properties.find(p => p.id === propertyId)?.name;
      this.propertyId = -1;

      this.portfolioId = entityDetails.propertyOrPortfolioId;
    }

    this.due = this.buildDueString(
      entityDetails.dueDate,
      this.workflowStep,
      entityDetails.dueDateBaseDate === DueDateBaseDate.CreationDate
    );

    entityDetails.createOrNotifyOn === -999
      ? (this.notify = 'As soon as NOTICE filed')
      : (this.notify = this.buildDueString(entityDetails.createOrNotifyOn, this.workflowStep));

    const notifyWhenDoneIds = entityDetails.notifyWhenDoneUserIds?.split(',') || [];
    this.notifyWhenDone = compact(notifyWhenDoneIds.map(id => users.find(u => u.id === Number(id))));
    this.assigneeUser = users.find(el => el.id === entityDetails?.actualAssigneeId);
    this.portfolioName = portfolios?.find(p => p?.id === this.portfolioId)?.name ?? '';
    this.unassigned =
      !this.entityDetails.actualAssigneeId &&
      this.entityDetails.assigneeDynamicAssigneeMode === DynamicInspectionAssigneeSelectionMode.ManualEntry;
  }

  private buildDueString(days: number, step: WorkflowStepEnumType, isCreationDate = false) {
    if (isCreationDate && days > 0) return `${Math.abs(days)}d after Inspection Creation Date`;

    let stepName = this.step;
    if (step === WorkflowStepEnumType.Notice) {
      const s = this.workflowSteps.find(s => s.enumerationValue === WorkflowStepEnumType.MoveOut);
      stepName = s?.displayText;
    }

    if (days < 0) {
      return `${Math.abs(days)}d before ${stepName.toUpperCase()}`;
    }
    if (days > 0) {
      return `${Math.abs(days)}d after ${stepName.toUpperCase()}`;
    }
    if (days === 0) {
      return `on ${stepName.toUpperCase()}`;
    }
  }
}
